// Maps Firebase Auth error codes to descriptive, user-facing messages.
// Firebase's default messages (e.g. "Firebase: Error (auth/wrong-password).")
// are technical and inconsistent across SDK versions, so we translate the
// known codes here and fall back to the raw message for anything unmapped.

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  "auth/invalid-email": "That email address doesn't look valid. Check for typos and try again.",
  "auth/user-disabled": "This account has been disabled. Contact your administrator for help.",
  "auth/user-not-found": "We couldn't find an account with that email address.",
  "auth/wrong-password": "That password is incorrect. Check it and try again.",
  "auth/invalid-credential": "That email or password is incorrect. Check them and try again.",
  "auth/invalid-login-credentials": "That email or password is incorrect. Check them and try again.",
  "auth/missing-password": "Enter your password to sign in.",
  "auth/too-many-requests": "Too many failed attempts. Wait a few minutes before trying again.",
  "auth/network-request-failed": "Couldn't reach the server. Check your internet connection and try again.",
  "auth/internal-error": "Something went wrong on our end. Please try again in a moment.",
  "auth/user-token-expired": "Your session expired. Please sign in again.",
  "auth/timeout": "The request timed out. Check your connection and try again.",
  "auth/quota-exceeded": "Sign-in is temporarily unavailable due to high demand. Please try again shortly.",
};

function extractFirebaseErrorCode(error: unknown): string | null {
  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "string" && code.startsWith("auth/")) return code;
  }
  return null;
}

/**
 * Converts a Firebase Auth (or generic) error into a descriptive,
 * human-readable message suitable for display in a login form.
 */
export function getAuthErrorMessage(error: unknown): string {
  const code = extractFirebaseErrorCode(error);
  if (code && AUTH_ERROR_MESSAGES[code]) {
    return AUTH_ERROR_MESSAGES[code];
  }

  const rawMessage = error instanceof Error ? error.message : null;

  if (code) {
    // Known auth/* code without a specific mapping — surface the code
    // alongside a generic explanation instead of Firebase's raw wording.
    const readableCode = code.replace("auth/", "").replace(/-/g, " ");
    return `Sign-in failed: ${readableCode}. Please try again or contact support if this continues.`;
  }

  if (rawMessage) {
    return rawMessage;
  }

  return "Sign-in failed. Please check your details and try again.";
}
